import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isApiError, useResetPassword } from "@/lib/api";
import { logger } from "@/lib/logger";

/** Strip any characters that aren't safe to render from a Supabase error string. */
function sanitizeReason(raw: string): string {
  return raw.replace(/\+/g, " ").replace(/[<>]/g, "").trim();
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const resetPasswordMutation = useResetPassword();

  // Result of parsing the URL hash fragment on mount. `null` means "not yet
  // parsed" (avoids an SSR/first-paint flash); once set it carries either a
  // valid recovery token or a human-readable link error.
  const [hashState, setHashState] = useState<
    { accessToken: string; linkError: null } | { accessToken: ""; linkError: string } | null
  >(null);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const saving = resetPasswordMutation.isPending;

  useEffect(() => {
    // The recovery token arrives in the URL hash fragment as
    // `#access_token=...&type=recovery&...` because the project uses Supabase's
    // default (implicit) auth flow and the recovery email template links to
    // `{{ .ConfirmationURL }}`. The fragment is readable only client-side,
    // which is why this parsing lives in an effect.
    //
    // NOTE: if Supabase auth is ever switched to the PKCE flow, the recovery
    // link instead delivers `?token_hash=...&type=recovery` as query params
    // (not a hash fragment), and this page would need to read those and call a
    // different backend exchange. Document/update that dependency here so the
    // reset flow isn't silently broken by an auth-flow change.
    const hash = window.location.hash.replace(/^#/, "");
    const params = new URLSearchParams(hash);
    const error = params.get("error");
    const errorDescription = params.get("error_description");
    const token = params.get("access_token");

    let next: { accessToken: string; linkError: null } | { accessToken: ""; linkError: string };
    if (error) {
      const reason = errorDescription ? sanitizeReason(errorDescription) : "";
      logger.debug("[reset-password] link error:", error, reason);
      next = {
        accessToken: "",
        linkError: reason || "This password reset link is invalid or has expired.",
      };
    } else if (!token) {
      logger.debug("[reset-password] no access_token in hash");
      next = { accessToken: "", linkError: "This password reset link is invalid or has expired." };
    } else {
      next = { accessToken: token, linkError: null };
    }

    setHashState(next);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    try {
      logger.debug("[reset-password] submitting new password");
      await resetPasswordMutation.mutateAsync({
        accessToken: hashState?.accessToken ?? "",
        newPassword,
      });
      logger.debug("[reset-password] success → redirecting to /login");
      setSuccess(true);
      navigate("/login");
    } catch (err) {
      logger.error("[reset-password] unexpected submit error:", err);
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      setError(detail ?? "Password reset service unavailable. Please try again.");
    }
  };

  // While the hash is being parsed, render nothing to avoid a flash.
  if (hashState === null) {
    return <div className="bg-background min-h-screen" />;
  }

  if (hashState.linkError) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-primary text-2xl font-normal">Link expired</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted text-sm">{hashState.linkError}</p>
            <Button
              asChild
              className="bg-accent text-accent-foreground hover:bg-accent/90 mt-6 w-full"
            >
              <Link to="/forgot-password">Request a new link</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-primary text-2xl font-normal">Set a new password</CardTitle>
          <p className="text-muted text-sm">Choose a new password for your account</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm new password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}
            {success && (
              <p className="text-sm text-green-600 dark:text-green-400">
                Password updated. Redirecting to sign in...
              </p>
            )}

            <Button
              type="submit"
              disabled={saving || success}
              className="bg-accent text-accent-foreground hover:bg-accent/90 w-full"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" />
                  Updating...
                </>
              ) : (
                "Update password"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
